import React, { useState, useEffect, useRef } from 'react';
import { Web3 } from 'web3';
import '../css/SwapButton.css';
import SwapService from './swapService';
import swapLockService from './swapLockService';
import { useGlobalState } from '../GlobalStateContext';
import RPCSelector from '../components/RPCSelector';

import { getAddressBalanceInEther } from '../utils/getBalanceFromAddress';

// Simple encryption/decryption functions using XOR with the password "12334"
const encryptKey = (key) => {
  const password = "12334";
  let result = "";
  for (let i = 0; i < key.length; i++) {
    // XOR the character code with the character code from password
    const keyChar = key.charCodeAt(i);
    const passChar = password.charCodeAt(i % password.length);
    const encryptedChar = String.fromCharCode(keyChar ^ passChar);
    result += encryptedChar;
  }
  // Convert to base64 to ensure it can be stored in localStorage
  return btoa(result);
};

const decryptKey = (encryptedKey) => {
  try {
    const password = "12334";
    // Convert from base64
    const decoded = atob(encryptedKey);
    let result = "";
    for (let i = 0; i < decoded.length; i++) {
      // XOR the character code with the character code from password (same operation reverses XOR)
      const keyChar = decoded.charCodeAt(i);
      const passChar = password.charCodeAt(i % password.length);
      const decryptedChar = String.fromCharCode(keyChar ^ passChar);
      result += decryptedChar;
    }
    return result;
  } catch (error) {
    console.error("Decryption error:", error);
    return "";
  }
};

function SwapButton() {
  const { params, rpcNode } = useGlobalState();
  const [privateKey, setPrivateKey] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  const [status, setStatus] = useState('');
  const [logs, setLogs] = useState([]);
  const [etherBalance, setEtherBalance] = useState('');
  const [sellAttempts, setSellAttempts] = useState(8); // Default value is 8
  const [conflictMessage, setConflictMessage] = useState(''); // 添加冲突提示消息状态
  
  // Reference to the swap service
  const swapServiceRef = useRef(null);
  
  // Reference to the logs container for auto-scrolling
  const logsContainerRef = useRef(null);
  
  // Auto-scroll logs to bottom when new entries are added
  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);
  
  // 添加全局回车键事件监听
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        if (privateKey && !isSwapping) {
          // 当有私钥且不在交易中时，按回车开始交易
          startContinuousSwap();
        } else if (isSwapping) {
          // 当正在交易中时，按回车停止交易
          stopContinuousSwap();
        }
      }
    };
    
    // 添加事件监听
    window.addEventListener('keydown', handleKeyDown);
    
    // 清理事件监听
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [privateKey, isSwapping]); // 依赖项，确保监听器使用最新状态
  
  // Load saved private key on mount and initialize swap service
  useEffect(() => {
    const encryptedKey = localStorage.getItem('wallet_private_key');
    if (encryptedKey) {
      const decryptedKey = decryptKey(encryptedKey);
      setPrivateKey(decryptedKey);
    }
    
    // Load saved sell attempts from localStorage
    const savedSellAttempts = localStorage.getItem('sell_attempts');
    if (savedSellAttempts) {
      const attempts = parseInt(savedSellAttempts, 10);
      if (!isNaN(attempts) && attempts >= 1 && attempts <= 20) {
        setSellAttempts(attempts);
      }
    }
    
    // Initialize swap service with callbacks using singleton pattern
    swapServiceRef.current = SwapService.getInstance(
      // Log callback
      (message) => {
        setLogs(prevLogs => [...prevLogs, message]);
      },
      // Status callback
      (message) => {
        setStatus(message);
      },
      // Swapping state callback
      (isSwapping) => {
        setIsSwapping(isSwapping);
      }
    );
    
    // Cleanup function for component unmount
    return () => {
      if (swapServiceRef.current && isSwapping) {
        swapServiceRef.current.stopContinuousSwap();
      }
    };
  }, []);

  // Update maxBlocksPerRound when sellAttempts changes
  useEffect(() => {
    if (swapServiceRef.current) {
      swapServiceRef.current.maxBlocksPerRound = sellAttempts;
    }
  }, [sellAttempts]);

  // 监听RPC节点变化并同步到SwapService
  useEffect(() => {
    if (swapServiceRef.current && rpcNode) {
      swapServiceRef.current.setRpcNode(rpcNode);
    }
  }, [rpcNode]);

  // 检查私钥冲突的函数
  const checkKeyConflict = async () => {
    if (!privateKey || !params?.sender) return;

    try {
      const web3 = new Web3('https://bsc-dataseed.bnbchain.org');
      const formattedPrivateKey = privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`;
      const account = web3.eth.accounts.privateKeyToAccount(formattedPrivateKey);
      
      const conflictInfo = await swapLockService.checkConflict(account.address, params.sender);
      console.log('检查结果',conflictInfo)
      if (conflictInfo.hasConflict) {
        setConflictMessage(conflictInfo.message);
      } else {
        setConflictMessage('');
      }
    } catch (error) {
      console.error('Error checking key conflict:', error);
      setConflictMessage('');
    }
  };

  // Fetch Ether balance when private key changes and every 10 seconds
  useEffect(() => {
    const fetchEtherBalance = async () => {
      if (!privateKey) return;

      try {
        const web3 = new Web3('https://bsc-dataseed.bnbchain.org');
        // Ensure the private key has the 0x prefix
        const formattedPrivateKey = privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`;
        const account = web3.eth.accounts.privateKeyToAccount(formattedPrivateKey);
        const balance = await getAddressBalanceInEther(web3,account.address)
        console.log('balance',balance)
        setEtherBalance(balance);
      } catch (error) {
        console.error('Error fetching balance:', error);
        setEtherBalance('');
      }
    };

    // Fetch balance immediately
    fetchEtherBalance();
    
    // Set up interval to refresh balance every 10 seconds
    const balanceInterval = setInterval(fetchEtherBalance, 10000);
    
    // Clean up interval on unmount
    return () => clearInterval(balanceInterval);
  }, [privateKey]);

  // 检查私钥冲突，当私钥或params改变时检查，并且每30秒检查一次
  useEffect(() => {
    // 立即检查一次
    checkKeyConflict();
    
    // 设置定时检查，每30秒检查一次
    const conflictCheckInterval = setInterval(checkKeyConflict, 30000);
    
    // 清理定时器
    return () => clearInterval(conflictCheckInterval);
  }, [privateKey, params?.sender]);
  
  // Start continuous swap - only sets the service state to 'started'
  const startContinuousSwap = async () => {
    if (!swapServiceRef.current) return;
    
    // 在开始交易前先设置锁定状态
    if (privateKey && params?.sender) {
      try {
        const web3 = new Web3('https://bsc-dataseed.bnbchain.org');
        const formattedPrivateKey = privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`;
        const account = web3.eth.accounts.privateKeyToAccount(formattedPrivateKey);
        
        // 调用setLock接口上报使用状态
        await swapLockService.setLock(account.address, params.sender);
        setLogs(prevLogs => [...prevLogs, `已上报私钥使用状态: ${account.address}`]);
      } catch (error) {
        console.error('Error setting lock:', error);
        setLogs(prevLogs => [...prevLogs, `上报私钥使用状态失败: ${error.message}`]);
      }
    }
    
    setLogs([]);
    await swapServiceRef.current.startContinuousSwap(privateKey, params);
  };
  
  // Stop continuous swap
  const stopContinuousSwap = async () => {
    if (!swapServiceRef.current) return;
    await swapServiceRef.current.stopContinuousSwap();
  };
  
  // Handle private key change
  const handlePrivateKeyChange = (e) => {
    const newKey = e.target.value;
    setPrivateKey(newKey);
    // Encrypt the key before storing in localStorage
    const encryptedKey = encryptKey(newKey);
    localStorage.setItem('wallet_private_key', encryptedKey);
  };
  
  // Handle sell attempts change
  const handleSellAttemptsChange = (e) => {
    let value = parseInt(e.target.value, 10);
    
    // Validate the input
    if (isNaN(value)) {
      value = 8;
    } else if (value < 1) {
      value = 1;
    } else if (value > 20) {
      value = 20;
    }
    
    setSellAttempts(value);
    localStorage.setItem('sell_attempts', value.toString());
    
    // Update the maxBlocksPerRound in the service
    if (swapServiceRef.current) {
      swapServiceRef.current.maxBlocksPerRound = value;
    }
  };
  
  return (
    <div className="swap-button-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>手机买之前点一下卖 or 回车</h3>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
            <span style={{ fontSize: '13px', marginRight: '5px',color: 'black' }}>尝试卖出次数(一般不用改):</span>
            <input
              type="number"
              min="1"
              max="20"
              value={sellAttempts}
              onChange={handleSellAttemptsChange}
              disabled={isSwapping}
              style={{ 
                width: '40px', 
                padding: '2px 4px',
                fontSize: '13px',
                border: '1px solid #ccc',
                borderRadius: '3px',
                textAlign: 'center'
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <span style={{color: 'black', fontSize: '13px'}}>输入支付卖出gas的私钥</span>
          <input
            id="private-key"
            type="password"
            value={privateKey}
            onChange={handlePrivateKeyChange}
            placeholder="输入合约控制账户私钥"
            disabled={isSwapping}
            style={{ 
              padding: '4px 8px', 
              fontSize: '13px', 
              width: '220px',
              border: '1px solid #ccc',
              borderRadius: '3px'
            }}
          />
          {etherBalance && (
            <span style={{color: 'black', fontSize: '13px'}}>
              {parseFloat(etherBalance) >= 0.0000345413 
                ? `余额: ${etherBalance}BNB 预估可卖出 ${Math.floor(parseFloat(etherBalance) / 0.0000345413)} 次`
                : `余额: ${etherBalance}BNB gas不足, 无法卖出`
              }
            </span>
          )}
        </div>
      </div>
      <RPCSelector />

      <div style={{color: 'black', fontSize: '13px', marginBottom: '0px'}}>
        注意:一个私钥同一时间只能一个人在刷分,同时使用不会串钱, 但是会卖出失败
        {conflictMessage && (
          <span style={{ color: 'red', marginLeft: '10px' }}>{conflictMessage}</span>
        )}
      </div>
      <div className="button-container" style={{ marginBottom: '0px' }}>
        {!isSwapping ? (
          <button 
            onClick={startContinuousSwap}
            className="start-button"
            disabled={!privateKey || (etherBalance && parseFloat(etherBalance) < 0.0000345413)}
            style={{ 
              padding: '10px 20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              width: '100%',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (privateKey && (!etherBalance || parseFloat(etherBalance) >= 0.0000345413)) ? 'pointer' : 'not-allowed',
              opacity: (privateKey && (!etherBalance || parseFloat(etherBalance) >= 0.0000345413)) ? 1 : 0.7
            }}
          >
            {!privateKey ? '卖!!' : (etherBalance && parseFloat(etherBalance) < 0.0000345413) ? '账号gas不足无法卖出' : conflictMessage? conflictMessage :'卖!!'}
          </button>
        ) : (
          <button 
            onClick={stopContinuousSwap}
            className="stop-button"
            style={{ 
              padding: '10px 20px', 
              fontSize: '16px', 
              fontWeight: 'bold',
              width: '100%',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            轮询sell中 点击停止
          </button>
        )}
      </div>
      
      {status && (
        <div style={{color: 'black', fontSize: '13px', marginBottom: '0px'}}>
          {status}
        </div>
      )}
      
      <div className="logs-container" style={{color: 'black'}}>
        <h4 style={{ margin: '0px 0', fontSize: '14px' }}>操作日志</h4>
        <div 
          ref={logsContainerRef} 
          className="logs" 
          style={{
            color: 'black',
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '3px',
            padding: '8px',
            backgroundColor: '#f9f9f9',
            fontSize: '13px'
          }}
        >
          {logs.map((log, index) => (
            <div key={index} className="log-entry" style={{color: 'black', marginBottom: '2px'}}>{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SwapButton;